import React, { useEffect, useState } from "react";
import { ArrowRight, CalendarDays, Clock, ExternalLink, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CALENDLY_URL } from "@/lib/brand";
import { PageHero, SectionHeader, EmeraldButton } from "@/components/Bits";
import { LIFESTYLE } from "@/lib/brand";

const BookConsultation = () => {
  const [tab, setTab] = useState("calendar"); // "calendar" | "form"
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_date: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Load Calendly inline-widget script once
  useEffect(() => {
    const id = "calendly-widget-js";
    if (document.getElementById(id)) return;
    const s = document.createElement("script");
    s.id = id;
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/bookings", form);
      toast.success(
        "Consultation request received. Dr. Foster's team will be in touch shortly."
      );
      setForm({ name: "", email: "", phone: "", preferred_date: "", message: "" });
    } catch (err) {
      toast.error("Could not submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-book">
      <PageHero
        overline="Book a Consultation"
        title="Let's build your exit strategy."
        subtitle="A private 30-minute conversation with Dr. Foster to assess where you are, where you'd like to go, and the highest-leverage next step."
        image={LIFESTYLE.office}
      />

      <section className="ess-section bg-ess-paper">
        <div className="ess-container">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-10">
            <SectionHeader
              overline="Schedule"
              title="Choose your preferred way to book."
              subtitle="Pick a time directly on Dr. Foster's calendar, or share your context first and we'll reach out personally."
              maxWidth="max-w-2xl"
            />
            <div className="inline-flex border border-ess-emerald/20 bg-white shrink-0">
              <button
                onClick={() => setTab("calendar")}
                data-testid="book-tab-calendar"
                className={`px-5 py-3 text-[11px] uppercase tracking-[0.22em] transition ${
                  tab === "calendar"
                    ? "bg-ess-emerald text-white"
                    : "text-ess-emerald hover:bg-ess-paper"
                }`}
              >
                Pick a Time
              </button>
              <button
                onClick={() => setTab("form")}
                data-testid="book-tab-form"
                className={`px-5 py-3 text-[11px] uppercase tracking-[0.22em] transition ${
                  tab === "form"
                    ? "bg-ess-emerald text-white"
                    : "text-ess-emerald hover:bg-ess-paper"
                }`}
              >
                Send a Request
              </button>
            </div>
          </div>

          {tab === "calendar" ? (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              <aside className="lg:col-span-4 space-y-6">
                <div className="bg-white p-6 border border-ess-blush">
                  <p className="ess-overline">What to Expect</p>
                  <div className="mt-5 space-y-4 text-sm">
                    <div className="flex items-start gap-3">
                      <Clock size={16} className="text-ess-gold-deep mt-1" />
                      <div>
                        <p className="font-display text-lg text-ess-emerald">30 minutes</p>
                        <p className="text-ess-slate">A focused, structured conversation.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CalendarDays size={16} className="text-ess-gold-deep mt-1" />
                      <div>
                        <p className="font-display text-lg text-ess-emerald">Virtual</p>
                        <p className="text-ess-slate">Scheduled at a time that honors your week.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Mail size={16} className="text-ess-gold-deep mt-1" />
                      <div>
                        <p className="font-display text-lg text-ess-emerald">No obligation</p>
                        <p className="text-ess-slate">You decide if and how we work together.</p>
                      </div>
                    </div>
                  </div>
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="book-calendly-external"
                    className="mt-6 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-ess-emerald ess-link"
                  >
                    Open in new tab <ExternalLink size={12} />
                  </a>
                </div>
              </aside>
              <div className="lg:col-span-8">
                <div
                  data-testid="book-calendly-widget"
                  className="calendly-inline-widget bg-white border border-ess-blush"
                  data-url={`${CALENDLY_URL}?primary_color=064e3b&text_color=1f2937&hide_event_type_details=0&hide_gdpr_banner=1`}
                  style={{ minWidth: "320px", height: "720px" }}
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              <aside className="lg:col-span-5">
                <div className="bg-white p-8 border border-ess-blush">
                  <p className="ess-overline">Prefer to share your context first?</p>
                  <p className="mt-4 text-ess-slate leading-relaxed text-sm">
                    Send us a brief request and our team will respond personally
                    with available times and a short intake to make the most of our
                    conversation.
                  </p>
                </div>
              </aside>
              <form
                onSubmit={submit}
                className="lg:col-span-7 bg-white p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5 border border-ess-blush"
                data-testid="booking-form"
              >
                <div className="md:col-span-2">
                  <label className="ess-overline">Full Name</label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    data-testid="book-name"
                    className="mt-2 w-full bg-ess-paper px-4 py-3 border border-ess-blush-deep focus:border-ess-emerald outline-none"
                  />
                </div>
                <div>
                  <label className="ess-overline">Email</label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    data-testid="book-email"
                    className="mt-2 w-full bg-ess-paper px-4 py-3 border border-ess-blush-deep focus:border-ess-emerald outline-none"
                  />
                </div>
                <div>
                  <label className="ess-overline">Phone</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    data-testid="book-phone"
                    className="mt-2 w-full bg-ess-paper px-4 py-3 border border-ess-blush-deep focus:border-ess-emerald outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="ess-overline">Preferred Date</label>
                  <input
                    type="date"
                    value={form.preferred_date}
                    onChange={(e) => setForm((f) => ({ ...f, preferred_date: e.target.value }))}
                    data-testid="book-date"
                    className="mt-2 w-full bg-ess-paper px-4 py-3 border border-ess-blush-deep focus:border-ess-emerald outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="ess-overline">What would you like to explore?</label>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    data-testid="book-message"
                    className="mt-2 w-full bg-ess-paper px-4 py-3 border border-ess-blush-deep focus:border-ess-emerald outline-none resize-none"
                  />
                </div>
                <div className="md:col-span-2 flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="book-submit"
                    className="bg-ess-emerald text-white px-8 py-4 text-[11px] uppercase tracking-[0.24em] hover:bg-ess-emerald-dark transition disabled:opacity-60 inline-flex items-center gap-3"
                  >
                    {submitting ? "Sending…" : "Send Request"} <ArrowRight size={14} />
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BookConsultation;
